'use client';

import React, { useEffect, useMemo, useState } from 'react';
import Typography from '@/components/atoms/typography';
import Link from '@/components/atoms/link';
import Markdown from '@/components/atoms/markdown';
import Accordion from '@/components/molecules/accordion';
import { ProjectBuilderGuideProps } from './types';
import { sdk } from '@/sdk';
import { useParams } from 'next/navigation';

const ProjectBuilderGuide = ({ projectbuilder }: ProjectBuilderGuideProps) => {
  const { locale } = useParams() as { locale?: string };

  const [fetchedProjectBuilder, setFetchedProjectBuilder] =
    useState<ProjectBuilderGuideProps['projectbuilder']>();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (projectbuilder || fetchedProjectBuilder) return;
      if (!locale) return;

      setIsLoading(true);

      try {
        sdk.defaultConfigure(locale);

        const res: any = await sdk.page.getPage({
          path: '/project-builder-guide',
        });

        if (cancelled || res?.isError) return;

        const pageData = res?.data;
        const dataSources = pageData?.data?.dataSources ?? {};

        const sections = [
          pageData?.page?.sections?.head,
          pageData?.page?.sections?.main,
          pageData?.page?.sections?.footer,
        ].filter(Boolean);

        let config: any;

        for (const section of sections) {
          for (const layoutElement of section?.layoutElements ?? []) {
            for (const tastic of layoutElement?.tastics ?? []) {
              if (
                tastic?.tasticType ===
                'commercetools/ui/content/project-builder-guide'
              ) {
                config = tastic?.configuration;
                break;
              }
            }
            if (config) break;
          }
          if (config) break;
        }

        if (!config) return;

        const dataSourceId = config?.data?.dataSourceId;
        const ds = dataSourceId ? dataSources?.[dataSourceId] : undefined;

        if (ds && !cancelled) {
          setFetchedProjectBuilder(ds);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [projectbuilder, fetchedProjectBuilder, locale]);

  const resolvedProjectBuilder = projectbuilder ?? fetchedProjectBuilder;
  const globalFaq = useMemo(() => {
    const section = resolvedProjectBuilder?.sections?.find(
      (s) => s?.global_faq
    );
    return section?.global_faq;
  }, [resolvedProjectBuilder]);

  if (!resolvedProjectBuilder && isLoading) {
    return (
      <div className="mt-20 px-5 md:px-20">
        <Typography className="text-center text-gray-600">
          Loading...
        </Typography>
      </div>
    );
  }
  if (!globalFaq) return null;

  return (
    <div id="faq-section" className="relative w-full">
      <div className="lg:col-span-9 lg:col-start-4 bg-white ">

        <p className="sticky top-0 z-20 bg-white border-b border-[#CCCCCC] py-5 text-primary-black text-left text-2xl font-medium leading-[2.375rem] sm:text-center sm:leading-[3.5rem]">
          {resolvedProjectBuilder?.title}
        </p>
        <div className="mt-11">
          {globalFaq?.accordion?.map((faq, index) => (
            <div key={index} className="w-full ">
              <Accordion
                className={`mr-0 !border-b border-t-0 border-l-0 border-r-0 border-[#dce0eb] `}
                key={index}
              >
                <Accordion.Button
                  className={`text-[21px] !px-0 !py-3 text-left text-[1.375rem] font-medium leading-8 text-[#333]`}
                >
                  <Typography as="h3" className="w-full">
                    {faq?.question}
                  </Typography>
                </Accordion.Button>
                <Accordion.Panel>
                  <Markdown className="text-[16px]">{faq?.answer}</Markdown>
                </Accordion.Panel>
              </Accordion>
            </div>
          ))}
        </div>
        {globalFaq?.title && (
          <Typography className=" !pb-5 !text-center 
                         text-base !font-normal 
                         leading-relaxed !text-[#090909]
                         sm:mt-[3.25rem] sm:!pb-[3.25rem]">
            {globalFaq.title}
          </Typography>
        )}
      </div>
    </div>
  );
};

export default ProjectBuilderGuide;